import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Button, Space, Card, Input, Typography, Select, message } from "antd";
import {
  PlusOutlined,
  DatabaseOutlined,
  PlusCircleOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { DragOverlay, DragStartEvent } from "@dnd-kit/core";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { v4 as uuidv4 } from "uuid";
import {
  Survey,
  SurveyItem,
  QuestionData,
  QuestionType,
  SectionData,
} from "../../../../types/survey";
import { fieldFactory } from "../../../../utils/fieldFactory";
import { ItemRenderer } from "../ItemRenderer";
import {
  useTemplateGetById,
  useTemplatesTableGet,
} from "../../../../hooks/api/surveys";
import { transformApiSurveyToApp } from "../../../../utils/transformSurveyToApi";
import { cloneSurveyWithNewIds } from "../../../../utils/cloneSurveyForTemplate";
import Loading from "../../Loading";
import { useSimpleModal } from "../../Modal/Simple";

const { Text } = Typography;

interface SurveyBuilderProps {
  isTemplate?: boolean;
  initialSurvey?: Survey;
  onChange: (survey: Survey) => void;
  onSave?: (survey: Survey) => void;
  setInitialSurvey?: (survey: Survey) => void;
}

const createEmptySurvey = (): Survey => ({
  status: "draft",
  title: "",
  items: [],
});

interface ValidationError {
  itemId: string;
  field: "title" | "questionTitle";
  message: string;
}

interface SortableItemWrapperProps {
  item: SurveyItem;
  index: number;
  onChange: (item: SurveyItem) => void;
  onDelete: () => void;
  onCopy: (item: SurveyItem, insertAfterIndex?: number) => void;
  onAddQuestion?: (type: QuestionType) => void;
  onUpdateQuestion?: (
    questionId: string,
    updates: Partial<QuestionData>,
  ) => void;
  onDeleteQuestion?: (questionId: string) => void;
  onCopyQuestionInSection?: (questionId: string) => void;
  availableQuestions?: Array<{ id: string; title: string }>;
  validationErrors?: ValidationError[];
}

const SortableItemWrapper: React.FC<SortableItemWrapperProps> = ({
  item,
  index,
  onChange,
  onDelete,
  onCopy,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onCopyQuestionInSection,
  availableQuestions,
  validationErrors = [],
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const itemErrors = validationErrors.filter((e) => e.itemId === item.id);

  return (
    <div ref={setNodeRef} style={style}>
      <ItemRenderer
        item={item}
        onChange={onChange}
        onDelete={onDelete}
        onCopy={onCopy}
        index={index}
        onAddQuestion={onAddQuestion}
        onUpdateQuestion={onUpdateQuestion}
        onDeleteQuestion={onDeleteQuestion}
        onCopyQuestionInSection={onCopyQuestionInSection}
        dragHandleProps={{ attributes, listeners }}
        availableQuestions={availableQuestions}
        validationErrors={itemErrors}
      />
    </div>
  );
};

export const SurveyBuilder: React.FC<SurveyBuilderProps> = ({
  initialSurvey,
  isTemplate,
  onChange,
  onSave,
  setInitialSurvey,
}) => {
  const [survey, setSurvey] = useState<Survey>(
    initialSurvey || createEmptySurvey(),
  );
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | undefined
  >();
  const { open, contextHolder } = useSimpleModal();
  const templatesGet = useTemplatesTableGet();
  const templateGetById = useTemplateGetById(selectedTemplateId);
  const onChangeRef = useRef(onChange);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<
    { value: string; label: string }[]
  >([]);

  const searchTimeoutRef = useRef<number | null>(null);

  const hasSurveyData = useMemo(() => {
    return survey.items.length > 0 || survey.title.trim() !== "";
  }, [survey.items, survey.title]);

  const handleSearch = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = window.setTimeout(() => {
      setSearchQuery(value);
      setPage(1);
      setTemplates([]);
      loadPage(1, value);
    }, 300);
  };

  const loadPage = async (pageNum: number, search?: string) => {
    setLoading(true);
    const res = await templatesGet.fetch({
      page: pageNum,
      pageSize: 10,
      search: search || searchQuery,
    });
    if (res?.data) {
      setTemplates((prev) =>
        pageNum === 1
          ? res.data.map((t) => ({ value: t.id, label: t.title }))
          : [
            ...prev,
            ...res.data.map((t) => ({ value: t.id, label: t.title })),
          ],
      );
      setTotal(res.total);
    }
    setLoading(false);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (open && templates.length === 0) {
      setPage(1);
      loadPage(1);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
      !loading &&
      templates.length < total
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadPage(nextPage, searchQuery);
    }
  };

  const validateSurveyTitle = useCallback((): ValidationError[] => {
    if (!survey.title || survey.title.trim() === "") {
      return [
        {
          itemId: "survey-title",
          field: "title",
          message: "Survey title is required",
        },
      ];
    }
    return [];
  }, [survey.title]);

  useEffect(() => {
    if (searchQuery === "") {
      templatesGet.fetch();
    }
  }, [searchQuery]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const notifyTimeoutRef = useRef<number | null>(null);

  const debouncedNotifyParent = useCallback((nextSurvey: Survey) => {
    if (notifyTimeoutRef.current !== null) {
      window.clearTimeout(notifyTimeoutRef.current);
    }
    notifyTimeoutRef.current = window.setTimeout(() => {
      onChangeRef.current(nextSurvey);
    }, 200);
  }, []);

  useEffect(
    () => () => {
      if (notifyTimeoutRef.current !== null) {
        window.clearTimeout(notifyTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    console.log("Builder received initialSurvey", initialSurvey);
    if (initialSurvey) {
      setSurvey(initialSurvey);
    }
  }, [initialSurvey]);

  useEffect(() => {
    if (!selectedTemplateId) return;

    const load = async () => {
      const res = await templateGetById.fetch(undefined, selectedTemplateId);

      if (!res) return;

      const survey = transformApiSurveyToApp(res);
      const cloned = cloneSurveyWithNewIds(survey);

      setSurvey(cloned);
      onChangeRef.current(cloned);
    };

    load();
  }, [selectedTemplateId]);

  const updateSurvey = useCallback(
    (updater: (s: Survey) => Survey) => {
      setSurvey((prev) => {
        const next = updater(prev);
        debouncedNotifyParent(next);
        return next;
      });
    },
    [debouncedNotifyParent],
  );

  const recalcSort = (items: SurveyItem[]): SurveyItem[] => {
    return items.map((item, index) => ({
      ...item,
      sort: index,
    }));
  };

  const validateQuestion = (
    question: QuestionData,
    itemId: string,
  ): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (!question.title || question.title.trim() === "") {
      errors.push({
        itemId,
        field: "questionTitle",
        message: "Question title is required",
      });
    }

    return errors;
  };

  const validateSection = (
    section: SectionData,
    itemId: string,
  ): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (!section.title || section.title.trim() === "") {
      errors.push({
        itemId,
        field: "title",
        message: "Section title is required",
      });
    }

    if (section.questions.length > 0) {
      const lastQuestion = section.questions[section.questions.length - 1];
      const questionErrors = validateQuestion(lastQuestion, itemId);
      errors.push(
        ...questionErrors.map((e) => ({
          ...e,
          field: "questionTitle" as const,
        })),
      );
    }

    return errors;
  };

  const validateLastItem = useCallback((): {
    valid: boolean;
    errors: ValidationError[];
  } => {
    if (survey.items.length === 0) {
      return { valid: true, errors: [] };
    }

    const lastItem = survey.items[survey.items.length - 1];
    let errors: ValidationError[] = [];

    if (lastItem.question) {
      errors = validateQuestion(lastItem.question, lastItem.id);
    } else if (lastItem.section) {
      errors = validateSection(lastItem.section, lastItem.id);
    }

    return { valid: errors.length === 0, errors };
  }, [survey.items]);

  const canAddNewItem = useMemo(() => {
    const { valid } = validateLastItem();
    return valid;
  }, [validateLastItem]);

  const showValidationErrors = useCallback(() => {
    const surveyTitleErrors = validateSurveyTitle();
    const lastItemErrors = validateLastItem().errors;

    const errors = [...surveyTitleErrors, ...lastItemErrors];
    setValidationErrors(errors);

    if (errors.length > 0) {
      if (surveyTitleErrors.length > 0) {
        message.error(
          "Please fill in the survey title before adding a new item",
        );
        return false;
      }
      const lastItem = survey.items[survey.items.length - 1];
      let errorMsg = "";

      if (lastItem?.question) {
        errorMsg = "Please fill in the question title before adding a new item";
      } else if (lastItem?.section) {
        const hasSectionError = lastItemErrors.some((e) => e.field === "title");
        const hasQuestionError = lastItemErrors.some(
          (e) => e.field === "questionTitle",
        );

        if (hasSectionError && hasQuestionError) {
          errorMsg =
            "Please fill in the section title and the last question's title";
        } else if (hasSectionError) {
          errorMsg =
            "Please fill in the section title before adding a new item";
        } else {
          errorMsg =
            "Please fill in the last question's title before adding a new item";
        }
      }

      message.error(errorMsg);
      return false;
    }

    return true;
  }, [validateLastItem, validateSurveyTitle, survey.items]);

  const clearValidationErrors = useCallback((itemId?: string) => {
    if (itemId) {
      setValidationErrors((prev) => prev.filter((e) => e.itemId !== itemId));
    } else {
      setValidationErrors([]);
    }
  }, []);

  const createSection = useCallback(() => {
    if (!showValidationErrors()) return;

    clearValidationErrors();
    const newSection = fieldFactory.section();
    updateSurvey((s) => ({
      ...s,
      items: recalcSort([...s.items, newSection]),
    }));
  }, [updateSurvey, showValidationErrors, clearValidationErrors]);

  const createStandaloneQuestion = useCallback(() => {
    if (!showValidationErrors()) return;

    clearValidationErrors();
    const newQuestion = fieldFactory.question("text");
    updateSurvey((s) => ({
      ...s,
      items: recalcSort([...s.items, newQuestion]),
    }));
  }, [updateSurvey, showValidationErrors, clearValidationErrors]);

  const updateItem = useCallback(
    (itemId: string, updates: Partial<SurveyItem>) => {
      clearValidationErrors(itemId);

      setSurvey((prev) => {
        const newItems = prev.items.map((item) => {
          if (item.id !== itemId) return item;
          return { ...item, ...updates };
        });

        const next = { ...prev, items: newItems };
        debouncedNotifyParent(next);
        return next;
      });
    },
    [debouncedNotifyParent, clearValidationErrors],
  );

  const deleteItem = useCallback(
    (itemId: string) => {
      clearValidationErrors(itemId);
      updateSurvey((s) => ({
        ...s,
        items: recalcSort(s.items.filter((item) => item.id !== itemId)),
      }));
    },
    [updateSurvey, clearValidationErrors],
  );

  const copyItem = useCallback(
    (copiedItem: SurveyItem, insertAfterIndex?: number) => {
      if (!showValidationErrors()) return;

      updateSurvey((s) => {
        const originalIndex = s.items.findIndex((i) => i.id === copiedItem.id);
        const insertIndex =
          insertAfterIndex !== undefined
            ? insertAfterIndex + 1
            : originalIndex === -1
              ? s.items.length
              : originalIndex + 1;

        const newItems = [...s.items];
        newItems.splice(insertIndex, 0, copiedItem);

        return {
          ...s,
          items: recalcSort(newItems),
        };
      });
    },
    [updateSurvey, showValidationErrors],
  );

  const addQuestionToSection = useCallback(
    (itemId: string, questionType: QuestionType = "text") => {
      const sectionItem = survey.items.find((i) => i.id === itemId);
      if (sectionItem?.section) {
        const errors = validateSection(sectionItem.section, itemId);
        if (errors.length > 0) {
          setValidationErrors(errors);

          const hasSectionError = errors.some((e) => e.field === "title");
          const hasQuestionError = errors.some(
            (e) => e.field === "questionTitle",
          );

          let errorMsg = "Please fill in ";
          if (hasSectionError) errorMsg += "the section title";
          if (hasSectionError && hasQuestionError) errorMsg += " and ";
          if (hasQuestionError) errorMsg += "the last question's title";

          message.error(errorMsg);
          return;
        }
      }

      clearValidationErrors(itemId);
      const newQuestion = fieldFactory.questionInSection(questionType);

      updateSurvey((s) => ({
        ...s,
        items: s.items.map((item) => {
          if (item.id === itemId && item.section) {
            return {
              ...item,
              section: {
                ...item.section,
                questions: [
                  ...item.section.questions,
                  { ...newQuestion, sort: item.section.questions.length },
                ],
              },
            };
          }
          return item;
        }),
      }));
    },
    [updateSurvey, survey.items, clearValidationErrors],
  );

  const updateQuestionInSection = useCallback(
    (itemId: string, questionId: string, updates: Partial<QuestionData>) => {
      clearValidationErrors(itemId);

      updateSurvey((s) => ({
        ...s,
        items: s.items.map((item) => {
          if (item.id === itemId && item.section) {
            return {
              ...item,
              section: {
                ...item.section,
                questions: item.section.questions.map((q) =>
                  q.id === questionId ? { ...q, ...updates } : q,
                ),
              },
            };
          }
          return item;
        }),
      }));
    },
    [updateSurvey, clearValidationErrors],
  );

  const deleteQuestionFromSection = useCallback(
    (itemId: string, questionId: string) => {
      updateSurvey((s) => ({
        ...s,
        items: s.items.map((item) => {
          if (item.id === itemId && item.section) {
            return {
              ...item,
              section: {
                ...item.section,
                questions: item.section.questions
                  .filter((q) => q.id !== questionId)
                  .map((q, idx) => ({ ...q, sort: idx })),
              },
            };
          }
          return item;
        }),
      }));
    },
    [updateSurvey],
  );

  const copyQuestionInSection = useCallback(
    (itemId: string, questionId: string) => {
      const sectionItem = survey.items.find((i) => i.id === itemId);
      if (sectionItem?.section) {
        const lastQuestion =
          sectionItem.section.questions[
          sectionItem.section.questions.length - 1
          ];
        if (
          lastQuestion &&
          (!lastQuestion.title || lastQuestion.title.trim() === "")
        ) {
          setValidationErrors([
            {
              itemId,
              field: "questionTitle",
              message: "Please fill in the last question's title",
            },
          ]);
          message.error(
            "Please fill in the last question's title before copying",
          );
          return;
        }
      }

      updateSurvey((s) => ({
        ...s,
        items: s.items.map((item) => {
          if (item.id === itemId && item.section) {
            const questionIndex = item.section.questions.findIndex(
              (q) => q.id === questionId,
            );
            const question = item.section.questions[questionIndex];

            if (!question) return item;

            const copiedQuestion: QuestionData = {
              ...question,
              id: uuidv4(),
              title: `${question.title} (Copy)`,
              answers: question.answers?.map((a) => ({
                ...a,
                id: uuidv4(),
              })),
            };

            const newQuestions = [...item.section.questions];
            newQuestions.splice(questionIndex + 1, 0, copiedQuestion);

            return {
              ...item,
              section: {
                ...item.section,
                questions: newQuestions.map((q, idx) => ({
                  ...q,
                  sort: idx,
                })),
              },
            };
          }
          return item;
        }),
      }));
    },
    [updateSurvey, survey.items],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = survey.items.findIndex((item) => item.id === active.id);
    const newIndex = survey.items.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newItems = arrayMove(survey.items, oldIndex, newIndex);

    setSurvey((prev) => {
      const next = { ...prev, items: recalcSort(newItems) };
      debouncedNotifyParent(next);
      return next;
    });
  };

  const getAllQuestions = (): Array<{ id: string; title: string }> => {
    const questions: Array<{ id: string; title: string }> = [];

    survey.items.forEach((item) => {
      if (item.question) {
        questions.push({
          id: item.question.id,
          title: item.question.title || "Untitled question",
        });
      }
      if (item.section) {
        item.section.questions.forEach((q) => {
          questions.push({
            id: q.id,
            title: q.title || "Untitled question",
          });
        });
      }
    });

    return questions;
  };

  const renderItems = () => {
    const result: React.ReactNode[] = [];
    let currentGroup: React.ReactNode[] = [];
    const allQuestions = getAllQuestions();

    const flushGroup = () => {
      if (currentGroup.length > 0) {
        result.push(
          <div
            key={`group-${result.length}`}
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: 6,
              border: "1px solid #E8E8E8",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              padding: 16,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {currentGroup}
            </div>
          </div>,
        );
        currentGroup = [];
      }
    };

    survey.items.forEach((item, index) => {
      const itemNode = (
        <SortableItemWrapper
          key={item.id}
          item={item}
          index={index}
          onChange={(updatedItem) => updateItem(item.id, updatedItem)}
          onDelete={() => deleteItem(item.id)}
          onCopy={copyItem}
          onAddQuestion={(type) => addQuestionToSection(item.id, type)}
          onUpdateQuestion={(qId, updates) =>
            updateQuestionInSection(item.id, qId, updates)
          }
          onDeleteQuestion={(qId) => deleteQuestionFromSection(item.id, qId)}
          onCopyQuestionInSection={(qId) => copyQuestionInSection(item.id, qId)}
          availableQuestions={allQuestions}
          validationErrors={validationErrors}
        />
      );

      if (item.section) {
        flushGroup();
        result.push(itemNode);
      } else {
        currentGroup.push(itemNode);
      }
    });

    flushGroup();
    return result;
  };

  const handleTemplateChange = (value: string | undefined) => {
    if (!value) {
      setSelectedTemplateId(undefined);
      return;
    }

    if (hasSurveyData) {
      open({
        icon: <ExclamationCircleFilled />,
        title: "Apply template and reset survey?",
        content:
          "Are you sure you want to apply template?Applying a template will replace all existing questions and content in this survey.",
        okText: "Apply & reset",
        cancelText: "Cancel",
        onOk: () => {
          setSelectedTemplateId(value);
        },
        onCancel: () => { },
      });
    } else {
      setSelectedTemplateId(value);
    }
  };

  const activeItem = useMemo(
    () => survey.items.find((item) => item.id === activeId),
    [activeId, survey.items],
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        gap: 20,
        padding: "0 80px 24px 24px",
      }}
    >
      <div style={{ maxWidth: 800, width: "100%" }}>
        <Card style={{ marginBottom: 16, paddingTop: 24 }}>
          <Space orientation="vertical" style={{ width: "100%" }}>
            {!isTemplate ? (
              <Space
                orientation="vertical"
                size="small"
                style={{
                  width: "100%",
                  padding: "16px 12px",
                  backgroundColor: "#F5F5F5",
                  borderRadius: 6,
                }}
              >
                <Text style={{ display: "block" }}>Template</Text>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select template"
                  value={selectedTemplateId}
                  onChange={handleTemplateChange}
                  allowClear
                  loading={loading}
                  showSearch={{
                    filterOption: false,
                    onSearch: handleSearch,
                  }}
                  onOpenChange={(open) => {
                    handleDropdownVisibleChange(open);
                    if (!open) {
                      setSearchQuery("");
                      handleSearch("");
                    }
                  }}
                  onPopupScroll={handleScroll}
                  options={templates}
                  notFoundContent={
                    loading ? "Loading..." : "No templates found"
                  }
                />
              </Space>
            ) : null}
            <Space
              orientation="vertical"
              size="small"
              style={{ width: "100%" }}
            >
              <Text style={{ display: "block" }}>
                {isTemplate ? "Template title" : "Survey title"}{" "}
                <span style={{ color: "#FF7875" }}>*</span>
              </Text>
              <Input
                value={survey.title}
                maxLength={150}
                onChange={(e) =>
                  updateSurvey((s) => ({ ...s, title: e.target.value }))
                }
                placeholder="Input name"
              />
            </Space>
          </Space>
          {contextHolder}
        </Card>

        {survey.items.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={survey.items.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: 24 }}
              >
                {renderItems()}
              </div>
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeItem ? (
                <div
                  style={{
                    width: "100%",
                    background: "#F5F5F5",
                    borderRadius: 6,
                    border: "1px solid #E8E8E8",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                    padding: "12px 16px",
                    minHeight: 50,
                    display: "flex",
                    alignItems: "center",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "grabbing",
                  }}
                >
                  {activeItem.question?.title ||
                    activeItem.section?.title ||
                    "Untitled"}
                  {activeItem.section && (
                    <span
                      style={{
                        marginLeft: "auto",
                        color: "#888",
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      Section
                    </span>
                  )}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
      <div style={{ position: "relative" }}>
        <div
          style={{
            position: "fixed",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <Button
            className="shadow-button"
            shape="circle"
            size="large"
            icon={<DatabaseOutlined style={{ fontSize: 21, padding: 5 }} />}
            onClick={createSection}
            disabled={!canAddNewItem}
            title={
              !canAddNewItem ? "Fill in the previous item first" : "Add section"
            }
            style={{
              width: 40,
              height: 40,
              border: "none",
              opacity: !canAddNewItem ? 0.5 : 1,
            }}
          />
          <Button
            className="shadow-button"
            shape="circle"
            size="large"
            icon={<PlusCircleOutlined style={{ fontSize: 21, padding: 5 }} />}
            onClick={createStandaloneQuestion}
            disabled={!canAddNewItem}
            title={
              !canAddNewItem
                ? "Fill in the previous item first"
                : "Add question"
            }
            style={{
              width: 40,
              height: 40,
              // boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              border: "none",
              opacity: !canAddNewItem ? 0.5 : 1,
            }}
          />
        </div>
      </div>
      {templateGetById.loading ? <Loading absolute /> : null}
    </div>
  );
};
